import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import FileUpload from '../common/FileUpload';
import ImageLink from '../common/ImageLink';
import { FriendAndPartnerDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useFileApi } from '../../hooks/useFileApi';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface FriendAndPartnerFormProps {
  initialValues: FriendAndPartnerDTO;
  onSubmit: (data: FriendAndPartnerDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  logo: {
    id: 'logo',
    title: 'Logo Image',
  },
  optIn: {
    id: 'opt-in',
    title: 'Opt-In Settings',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

const FriendAndPartnerForm: React.FC<FriendAndPartnerFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<FriendAndPartnerDTO>(initialValues);
  const [uploadingImage, setUploadingImage] = useState(false);

  const { uploadFile } = useFileApi();

  const validationRules: ValidationRules = {
    name: [{ required: true, message: 'Name is required' }],
    websiteUrl: [{ required: true, message: 'Website URL is required' }],
    imageFileRecordId: [{ required: true, message: 'Logo image is required' }],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      { custom: (value) => Number(value) >= 0, message: 'Display Position must be a positive number' },
    ],
  };

  const { errors, validate } = useFormValidation<FriendAndPartnerDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingImage(true);
      const response = await uploadFile('files', file.name, file, 'friends-and-partners');
      if (response?.success && response.data) {
        setFormData((prev) => ({ ...prev, imageFileRecordId: response.data }));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setUploadingImage(false);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>

      <FormSection id={FORM_SECTIONS.basicInfo.id} title={FORM_SECTIONS.basicInfo.title}>
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter partner name"
          required
          error={errors.name}
        />
        <FormField
          id="websiteUrl"
          label="Website URL"
          type="text"
          value={formData.websiteUrl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="https://..."
          required
          error={errors.websiteUrl}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.logo.id} title={FORM_SECTIONS.logo.title} columns={1}>
        {formData.imageFileRecordId && (
          <ImageLink fileId={formData.imageFileRecordId} label="Current Logo" />
        )}
        <FileUpload
          id="imageFileRecordId"
          title="Upload or drag and drop partner logo"
          accept={FileTypeConstants.IMAGES}
          onChange={handleImageChange}
          isUploading={uploadingImage}
          required={!formData.imageFileRecordId}
          error={errors.imageFileRecordId}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.optIn.id} title={FORM_SECTIONS.optIn.title}>
        <FormField
          id="allowOptIn"
          label="Allow Opt-In"
          type="checkbox"
          value={formData.allowOptIn || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder=""
          helpText="If enabled, members can opt in to this partner's service during registration or renewal"
        />
        <FormField
          id="optInUrl"
          label="Opt-In URL"
          type="text"
          value={formData.optInUrl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="https://..."
          helpText="The URL members are directed to when opting in"
        />
        <FormField
          id="optInDescription"
          label="Opt-In Description"
          type="textarea"
          value={formData.optInDescription || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter the text shown next to the opt-in checkbox and in renewal emails"
          helpText="Used as the checkbox label on registration/renewal and as the promotional text in invoice emails"
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.settings.id} title={FORM_SECTIONS.settings.title}>
        <FormField
          id="displayPosition"
          label="Display Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="0"
          required
          error={errors.displayPosition}
        />
        <FormField
          id="showOnMemberDashboard"
          label="Show on Member Dashboard"
          type="checkbox"
          value={formData.showOnMemberDashboard || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder=""
          helpText="Display this partner's logo on the member portal dashboard"
        />
        <FormField
          id="isActive"
          label="Is Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder=""
        />
      </FormSection>

    </Form>
  );
};

export default FriendAndPartnerForm;
